<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import { ref, reactive } from "vue";
import * as yup from "yup";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import functions from "addeus-common-library/stores/function";
import HomeIcon from "../../assets/website/contact/contact-icon-1.svg";
import KartIcon from "../../assets/website/contact/contact-icon-2.svg";
import VInput from "addeus-common-library/components/base/form/VInput.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { useNotyf } from "addeus-common-library/composable/useNotyf";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits(["update:open"]);
const open = useVModel(props, "open", emit);
const { translate } = useTranslate();
const notif = useNotyf();

const formSchema = yup.object({
    firstName: yup.string().required("required"),
    lastName: yup.string().required("required"),
    email: yup.string().email("email").required("required"),
    phoneNumber: yup
        .string()
        .required("required")
        .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, "phoneNumber"),
    message: yup.string().required("required"),
    hasExperience: yup.boolean(),
    profil: yup.string().required("required"),
    actualJob: yup.string().required("required"),
    personalAmount: yup.number().typeError("required").required("required"),
    city: yup.string().required("required"),
    hasLocation: yup.boolean(),
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallerThan = breakpoints.smallerOrEqual("lg");
// import SignupVideo from "/@src/assets/video/signup.mp4";

const isLoading = ref(false);

const form = reactive({
    owner: undefined,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    hasExperience: false,
    profil: "",
    actualJob: "",
    personalAmount: undefined,
    city: "",
    hasLocation: false,
    message: "",
});

async function handle() {
    isLoading.value = true;
    try {
        await functions.sendEmailContactOwnerV2(form);
    } catch (error) {
        notif.error(translate("speed2max.contactForm.error").value);
    }
    isLoading.value = false;
    open.value = false;
}
</script>

<template>
    <VModal
        :open="open"
        size="giant"
        :title="translate('speed2max.formContactOwner.title').value"
        @close="open = false">
        <template #content>
            <TranslateNamespace path="speed2max.formContactOwner">
                <VFlex flex-direction="column" class="login-column p-6">
                    <div class="px-6">
                        <form @submit.prevent="handle">
                            <VValidation
                                v-slot="{ field: formFields }"
                                v-model="form"
                                property="signup"
                                :schema="formSchema">
                                <div class="submit-form">
                                    <TranslateNamespace path=".personnalInformation">
                                        <div
                                            class="is-size-4 is-weight-600 is-uppercase mb-5 has-text-dark">
                                            <img
                                                :src="HomeIcon"
                                                alt="home icon"
                                                style="
                                                    height: 30px;
                                                    margin: 0 10px 0 0;
                                                " />
                                            <Translate>.title</Translate>
                                        </div>
                                        <VFlex flex-direction="column">
                                            <VFlex
                                                :flex-direction="
                                                    smallerThan ? 'column' : 'row'
                                                "
                                                column-gap="10px"
                                                justify-content="space-between">
                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.lastName"
                                                    property="lastName"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.lastName">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.lastName.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".lastName.placeholder"
                                                                type="text"
                                                                autocomplete="lastName" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.lastName.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>

                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.firstName"
                                                    property="firstName"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.firstName">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.firstName.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".firstName.placeholder"
                                                                type="text"
                                                                autocomplete="firstName" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.firstName.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>
                                            </VFlex>

                                            <VFlex
                                                :flex-direction="
                                                    smallerThan ? 'column' : 'row'
                                                "
                                                column-gap="10px"
                                                justify-content="space-between">
                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.email"
                                                    property="email"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.email">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.email.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".email.placeholder"
                                                                type="text"
                                                                autocomplete="email" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.email.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>

                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.phoneNumber"
                                                    property="phoneNumber"
                                                    class="is-flex"
                                                    :schema="
                                                        formSchema.fields.phoneNumber
                                                    ">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.phoneNumber.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VTelInput
                                                                v-model="field.value"
                                                                placeholder=".phoneNumber.placeholder"
                                                                autocomplete="phoneNumber" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.phoneNumber.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>
                                            </VFlex>
                                        </VFlex>
                                    </TranslateNamespace>
                                    <TranslateNamespace path=".project">
                                        <div
                                            class="is-size-4 is-weight-600 is-uppercase my-5 has-text-dark">
                                            <img
                                                alt="kart icon"
                                                :src="KartIcon"
                                                style="
                                                    height: 30px;
                                                    margin: 0 10px 0 0;
                                                " />
                                            <Translate>.title</Translate>
                                        </div>
                                        <VFlex flex-direction="column">
                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.hasExperience"
                                                property="hasExperience"
                                                :schema="formSchema.fields.hasExperience">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.hasExperience.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VRadioGroup
                                                            v-model="field.value">
                                                            <VRadio
                                                                v-model="field.value"
                                                                name="hasExperience"
                                                                :value="true">
                                                                <Translate
                                                                    >.hasExperience.yes</Translate
                                                                >
                                                            </VRadio>
                                                            <VRadio
                                                                v-model="field.value"
                                                                name="hasExperience"
                                                                :value="false"
                                                                ><Translate
                                                                    >.hasExperience.no</Translate
                                                                ></VRadio
                                                            >
                                                        </VRadioGroup>
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.hasExperience.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>

                                            <VFlex
                                                :flex-direction="
                                                    smallerThan ? 'column' : 'row'
                                                "
                                                column-gap="10px"
                                                justify-content="space-between">
                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.owner"
                                                    property="owner"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.profil">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.profil.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".profil.placeholder" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.profil.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>

                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.actualJob"
                                                    property="actualJob"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.actualJob">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.actualJob.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".actualJob.placeholder" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.actualJob.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>
                                            </VFlex>

                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.message"
                                                property="message"
                                                :schema="formSchema.fields.message">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.message.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VTextarea
                                                            v-model="field.value"
                                                            :placeholder="
                                                                translate(
                                                                    'speed2max.formContactOwner.project.message.placeholder',
                                                                ).value
                                                            " />
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.message.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>

                                            <VFlex
                                                :flex-direction="
                                                    smallerThan ? 'column' : 'row'
                                                "
                                                column-gap="10px"
                                                justify-content="space-between">
                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.personalAmount"
                                                    property="owner"
                                                    class="is-flex"
                                                    :schema="
                                                        formSchema.fields.personalAmount
                                                    ">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.personalAmount.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".personalAmount.placeholder"
                                                                type="number" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.personalAmount.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>

                                                <VValidation
                                                    v-slot="{ field }"
                                                    v-model="form.city"
                                                    property="owner"
                                                    class="is-flex"
                                                    :schema="formSchema.fields.city">
                                                    <VField
                                                        class="mb-5"
                                                        style="width: 100%">
                                                        <VLabel>
                                                            <Translate
                                                                >.city.label</Translate
                                                            >
                                                        </VLabel>
                                                        <VControl
                                                            :has-error="
                                                                field.errors.length > 0 &&
                                                                field.isDirty
                                                            ">
                                                            <VInput
                                                                v-model="field.value"
                                                                placeholder=".city.placeholder" />
                                                        </VControl>
                                                        <p
                                                            v-for="error in field.errors"
                                                            :key="error"
                                                            class="help is-danger">
                                                            <Translate
                                                                >.city.validation.{{
                                                                    error
                                                                }}</Translate
                                                            >
                                                        </p>
                                                    </VField>
                                                </VValidation>
                                            </VFlex>

                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.hasLocation"
                                                property="hasLocation"
                                                :schema="formSchema.fields.hasLocation">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.hasLocation.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VRadioGroup
                                                            v-model="field.value">
                                                            <VRadio
                                                                v-model="field.value"
                                                                name="hasLocation"
                                                                :value="true"
                                                                >Oui</VRadio
                                                            >
                                                            <VRadio
                                                                v-model="field.value"
                                                                name="hasLocation"
                                                                :value="false"
                                                                >Non</VRadio
                                                            >
                                                        </VRadioGroup>
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.hasLocation.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>
                                        </VFlex>
                                    </TranslateNamespace>
                                    <VFlex
                                        flex-direction="row"
                                        justify-content="start"
                                        align-items="start">
                                        <VButton
                                            id="signup-button"
                                            class="px-6"
                                            :disabled="
                                                formFields.hasChildErrors() ||
                                                !formFields.isDirty
                                            "
                                            :loading="isLoading"
                                            color="primary"
                                            type="submit"
                                            size="huge"
                                            bold
                                            raised>
                                            <Translate path=".submit"></Translate
                                        ></VButton>
                                    </VFlex>
                                </div>
                            </VValidation>
                        </form>
                    </div>
                </VFlex>
            </TranslateNamespace>
        </template>
    </VModal>
</template>


<style lang="scss">
.v-modal {
    input {
        height: 50px !important;
    }

    .w-100 {
        width: 100%;
    }

    button > span {
        width: 100%;
    }

    .form-icon {
        padding-top: 10px;
    }

    > .modal-background {
        border: 0;
    }

    > .modal-content {
        > .modal-card {
            > .modal-card-head {
                border-bottom: 0;
            }

            > .modal-card-body {
                padding-top: 0;

                > .inner-content {
                    > div {
                        > .copy-block {
                            max-width: 450px;
                        }
                    }
                }
            }
        }
    }
}
</style>
